import { DATE_FORMATS } from '../../utils/Application_Constants';
import formatDate, { formatUtcDate } from '../../utils/formatDate';
import { currencyFormatter } from '../../utils/helpers';
import { PROCESSED_TIMESHEET_STATUS_LABELS } from '../constants';
import { rateTypesEnumApiToDisplay } from '../Configurations/processedTimesheetsConfig';

export const BLANK_DISPLAY_VALUE = '(blank)';
export const SYSTEM_USER = 'System';
export const CHANGE_KIND = {
    added: 'added',
    removed: 'removed',
    updated: 'updated',
};

export const HISTORY_EVENT_TYPE = {
    create: 'create',
    update: 'update',
    delete: 'delete',
};

export const HISTORY_ENTITY = {
    processedTimesheet: 'ProcessedTimesheet',
    processedTimesheetEntry: 'ProcessedTimesheetEntry',
    processedMileageEntry: 'ProcessedMileageEntry',
    comment: 'Comment',
    attachment: 'Attachment',
};

export const PROCESSED_TIMESHEET_HISTORY_FIELD_MAP = {
    status: { label: 'Status', type: 'status' },
    needsreviewstatus: { label: 'Needs Review Status', type: 'status' },
    statuschangereason: { label: 'Status Change Reason', type: 'comment' },
    payrolldate: { label: 'Payroll Date', type: 'date' },
    approvaldate: { label: 'Approval Date', type: 'date' },
    monthposted: { label: 'Month Posted', type: 'date' },
    monthposteddate: { label: 'Month Posted', type: 'date' },
    weekendingdate: { label: 'Week Ending Date', type: 'date' },
    submitteddate: { label: 'Submitted Date', type: 'datetime' },
    originalpaydate: { label: 'Original Pay Date', type: 'date' },
    accountingcase: { label: 'Accounting Case', type: 'string' },
    creditmemomode: { label: 'Credit Memo Mode', type: 'string' },
    specialprocessing: { label: 'Special Processing', type: 'string' },
    splitbyworklocation: { label: 'Split By Work Location', type: 'boolean' },
    ispapertimesheet: { label: 'Paper Timesheet', type: 'boolean' },
    iscorrection: { label: 'Correction', type: 'boolean' },
    lastmodified: { label: 'Last Modified', type: 'datetime' },
    date: { label: 'Date', type: 'date' },
    transactiondate: { label: 'Date', type: 'date' },
    hours: { label: 'Hours', type: 'number' },
    units: { label: 'Units', type: 'number' },
    miles: { label: 'Miles', type: 'number' },
    comments: { label: 'Comments', type: 'comment' },
    comment: { label: 'Comment', type: 'comment' },
    ratetype: { label: 'Rate Type', type: 'rateType' },
    ratetypeid: { label: 'Rate Type', type: 'rateTypeId' },
    ratetypecode: { label: 'Pay Type ID', type: 'string' },
    paytypeid: { label: 'Pay Type ID', type: 'string' },
    billtime: { label: 'Bill Time/Units', type: 'number' },
    billunit: { label: 'Bill Unit', type: 'rateType' },
    billamount: { label: 'Bill Amount', type: 'currency' },
    billrate: { label: 'Bill Rate', type: 'currency' },
    billmiles: { label: 'Bill Miles', type: 'number' },
    paytime: { label: 'Pay Time/Units', type: 'number' },
    payunit: { label: 'Pay Unit', type: 'rateType' },
    payamount: { label: 'Pay Amount', type: 'currency' },
    payrate: { label: 'Pay Rate', type: 'currency' },
    paymiles: { label: 'Pay Miles', type: 'number' },
    isbillable: { label: 'Billable', type: 'boolean' },
    isholidayrelated: { label: 'Processed As Holiday', type: 'boolean' },
    processedasholiday: { label: 'Processed As Holiday', type: 'boolean' },
    locationid: { label: 'Work Location', type: 'locationId' },
    locationname: { label: 'Work Location', type: 'string' },
    worklocationid: { label: 'Work Location', type: 'locationId' },
    worklocationname: { label: 'Work Location', type: 'string' },
    discountamount: { label: 'Discount Amount', type: 'currency' },
    rebateamount: { label: 'Rebate Amount', type: 'currency' },
    ab5amount: { label: 'AB5 Amount', type: 'currency' },
    invoicestatus: { label: 'Invoice Status', type: 'string' },
    isposted: { label: 'Posted', type: 'boolean' },
    ismileage: { label: 'Mileage', type: 'boolean' },
    malpracticeamount: { label: 'Malpractice Amount', type: 'currency' },
    documenttype: { label: 'Document Type', type: 'string' },
};

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;
const TRUE_VALUES = new Set(['true', 'yes', '1']);
const FALSE_VALUES = new Set(['false', 'no', '0']);
const STATUS_ONLY_FIELDS = new Set(['status', 'needsreviewstatus']);
const ENTRY_ENTITIES = new Set([
    HISTORY_ENTITY.processedTimesheetEntry,
    HISTORY_ENTITY.processedMileageEntry,
]);

export const humanizeFieldName = (fieldName = '') =>
    fieldName
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/(\d)([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (character) => character.toUpperCase());

export const getHistoryFieldConfig = (fieldName) => {
    if (!fieldName) {
        return { label: '', type: 'string' };
    }

    const mappedField = PROCESSED_TIMESHEET_HISTORY_FIELD_MAP[fieldName.toLowerCase()];
    if (mappedField) {
        return mappedField;
    }

    return { label: humanizeFieldName(fieldName), type: 'string' };
};

const isBlankValue = (value) =>
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    value === 'null' ||
    value === 'undefined';

export const stripHtmlTags = (value) => {
    if (typeof value !== 'string') {
        return value;
    }

    const withoutTags = value
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (typeof document === 'undefined') {
        return withoutTags;
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = withoutTags;
    return textarea.value.trim();
};

const getChangeKind = (oldValue, newValue) => {
    const oldValueBlank = isBlankValue(oldValue);
    const newValueBlank = isBlankValue(newValue);
    if (oldValueBlank && !newValueBlank) {
        return CHANGE_KIND.added;
    }
    if (!oldValueBlank && newValueBlank) {
        return CHANGE_KIND.removed;
    }
    return CHANGE_KIND.updated;
};

const formatBooleanValue = (value) => {
    const normalized = String(value).trim().toLowerCase();
    if (TRUE_VALUES.has(normalized)) return 'Yes';
    if (FALSE_VALUES.has(normalized)) return 'No';
    return String(value);
};

const compactStatusKey = (value) =>
    String(value)
        .replace(/[\s_-]/g, '')
        .toLowerCase();

const formatStatusValue = (value) => {
    const raw = String(value).trim();
    if (PROCESSED_TIMESHEET_STATUS_LABELS[raw]) {
        return PROCESSED_TIMESHEET_STATUS_LABELS[raw];
    }

    const compact = compactStatusKey(raw);
    const matchingKey = Object.keys(PROCESSED_TIMESHEET_STATUS_LABELS).find(
        (key) => compactStatusKey(key) === compact
    );
    if (matchingKey) {
        return PROCESSED_TIMESHEET_STATUS_LABELS[matchingKey];
    }

    const matchingLabel = Object.values(PROCESSED_TIMESHEET_STATUS_LABELS).find(
        (label) => compactStatusKey(label) === compact
    );
    if (matchingLabel) {
        return matchingLabel;
    }

    return raw.toUpperCase();
};

const parseHistoryNumber = (value) => {
    if (typeof value === 'number') {
        return value;
    }

    const raw = String(value).trim();
    const parsedValue = Number(raw);
    if (!Number.isNaN(parsedValue)) {
        return parsedValue;
    }

    if ((raw.match(/,/g) || []).length === 1 && !raw.includes('.')) {
        return Number(raw.replace(',', '.'));
    }

    return NaN;
};

const formatNumberValue = (value) => {
    const parsedValue = parseHistoryNumber(value);
    if (Number.isNaN(parsedValue)) return String(value);
    if (parsedValue % 1 === 0) return String(parsedValue);
    return parsedValue.toFixed(2);
};

const toLookupKey = (value) => {
    if (isBlankValue(value)) {
        return null;
    }
    const numeric = Number(value);
    return Number.isNaN(numeric) ? String(value) : numeric;
};

export const buildHistoryLookups = ({ locations = [], availableRates = [], entries = [] } = {}) => {
    const locationsById = new Map();
    locations.forEach((location) => {
        const key = toLookupKey(location?.locationId ?? location?.id);
        if (key !== null && !locationsById.has(key)) {
            locationsById.set(key, location);
        }
    });

    const ratesById = new Map();
    availableRates.forEach((rate) => {
        const key = toLookupKey(rate?.id);
        if (key !== null && !ratesById.has(key)) {
            ratesById.set(key, rate);
        }
    });
    entries.forEach((entry) => {
        const key = toLookupKey(entry?.rateTypeId);
        if (key === null || ratesById.has(key) || isBlankValue(entry?.rateType)) {
            return;
        }
        ratesById.set(key, {
            id: entry.rateTypeId,
            name: entry.rateType,
            displayName: entry.rateType,
        });
    });

    return { locationsById, ratesById };
};

const getRateTypeName = (rateTypeId, lookups = {}) => {
    const rate = lookups.ratesById?.get(toLookupKey(rateTypeId));
    return rate?.displayName || rate?.name || null;
};

export const formatRateTypeIdDisplay = (value, lookups = {}) => {
    if (isBlankValue(value)) {
        return BLANK_DISPLAY_VALUE;
    }
    const name = getRateTypeName(value, lookups);
    return name ? `${name} (${value})` : String(value);
};

export const formatLocationIdDisplay = (value, lookups = {}) => {
    if (isBlankValue(value)) {
        return BLANK_DISPLAY_VALUE;
    }
    const location = lookups.locationsById?.get(toLookupKey(value));
    const locationName = location?.locationName || location?.name;
    return locationName ? `${locationName} (${value})` : `Work Location ${value}`;
};

export const formatHistoryValue = (value, type, lookups = {}) => {
    if (isBlankValue(value)) {
        return BLANK_DISPLAY_VALUE;
    }

    switch (type) {
        case 'boolean':
            return formatBooleanValue(value);
        case 'status':
            return formatStatusValue(value);
        case 'currency': {
            const parsedValue = parseHistoryNumber(value);
            if (Number.isNaN(parsedValue)) return String(value);
            return currencyFormatter(parsedValue);
        }
        case 'number':
            return formatNumberValue(value);
        case 'date':
            return formatUtcDate(value, DATE_FORMATS.LT) || String(value);
        case 'datetime':
            return formatDate(value, { format: DATE_FORMATS.LT_DATE_AND_TIME }) || String(value);
        case 'rateType':
            return rateTypesEnumApiToDisplay(value);
        case 'rateTypeId':
            return formatRateTypeIdDisplay(value, lookups);
        case 'locationId':
            return formatLocationIdDisplay(value, lookups);
        case 'comment': {
            const strippedValue = stripHtmlTags(value);
            return isBlankValue(strippedValue) ? BLANK_DISPLAY_VALUE : strippedValue;
        }
        default: {
            if (typeof value === 'string' && ISO_DATE_PATTERN.test(value.trim())) {
                const hasTime = value.includes('T') && !value.includes('T00:00:00');
                return hasTime
                    ? formatDate(value, { format: DATE_FORMATS.LT_DATE_AND_TIME }) || value
                    : formatUtcDate(value, DATE_FORMATS.LT) || value;
            }
            if (typeof value === 'boolean') {
                return formatBooleanValue(value);
            }
            return String(value);
        }
    }
};

export const normalizeHistoryChange = (change = {}) => ({
    fieldName: change.fieldName ?? change.FieldName ?? '',
    oldValue: change.oldValue ?? change.OldValue,
    newValue: change.newValue ?? change.NewValue,
});

export const normalizeHistoryExtra = (extra = {}) => ({
    id: extra.id ?? extra.Id,
    locationId: extra.locationId ?? extra.LocationId,
    date: extra.date ?? extra.Date,
    rateType: extra.rateType ?? extra.RateType,
    content: extra.content ?? extra.Content,
    name: extra.name ?? extra.Name,
});

export const normalizeHistoryEvent = (record = {}) => {
    const extra = record.extra ?? record.Extra;
    return {
        entity: record.entity ?? record.Entity ?? '',
        type: String(record.type ?? record.Type ?? '').toLowerCase(),
        modifiedBy: record.modifiedBy ?? record.ModifiedBy,
        modifiedAt: record.modifiedAt ?? record.ModifiedAt,
        changes: (record.changes ?? record.Changes ?? []).map(normalizeHistoryChange),
        extra: extra ? normalizeHistoryExtra(extra) : {},
    };
};

const getModifiedByDisplay = (modifiedBy) => {
    if (isBlankValue(modifiedBy)) {
        return SYSTEM_USER;
    }
    return String(modifiedBy).trim();
};

const getModifiedAtTime = (modifiedAt) => {
    if (!modifiedAt) {
        return 0;
    }
    const occurredAt = new Date(modifiedAt).getTime();
    return Number.isNaN(occurredAt) ? 0 : occurredAt;
};

const formatEntryDate = (value) =>
    formatUtcDate(value, DATE_FORMATS.MD) || formatHistoryValue(value, 'date');

const getLocationName = (locationId, lookups = {}) => {
    const location = lookups.locationsById?.get(toLookupKey(locationId));
    return location?.locationName || location?.name || null;
};

export const buildEntryDescription = (event, lookups = {}) => {
    if (!ENTRY_ENTITIES.has(event.entity)) {
        return '';
    }

    const extra = event.extra || {};
    const datePart = !isBlankValue(extra.date) ? `(${formatEntryDate(extra.date)})` : null;
    let rateName = null;
    if (!isBlankValue(extra.rateType)) {
        rateName = getRateTypeName(extra.rateType, lookups) || String(extra.rateType);
    } else if (event.entity === HISTORY_ENTITY.processedMileageEntry) {
        rateName = 'Mileage';
    }
    const locationName = !isBlankValue(extra.locationId)
        ? getLocationName(extra.locationId, lookups) || `Work Location ${extra.locationId}`
        : null;

    const parts = [];
    if (datePart) {
        parts.push(datePart);
    }
    if (rateName) {
        parts.push(rateName);
    }
    if (locationName) {
        parts.push(parts.length ? `at ${locationName}` : locationName);
    }

    return parts.join(' ');
};

const buildSubjectDescription = (event, lookups = {}) => {
    if (event.entity === HISTORY_ENTITY.attachment && !isBlankValue(event.extra?.name)) {
        return String(event.extra.name).trim();
    }

    return buildEntryDescription(event, lookups);
};

const appendSubjectDescription = (title, subjectDescription) =>
    subjectDescription ? `${title} • ${subjectDescription}` : title;

const buildDetailText = ({ label, previousValue, nextValue, changeKind }) => {
    if (changeKind === CHANGE_KIND.added) {
        return `${label} (added): ${nextValue}`;
    }
    if (changeKind === CHANGE_KIND.removed) {
        return `${label}: ${previousValue}`;
    }
    return `${label}: ${previousValue} → ${nextValue}`;
};

const mapChangeToDetail = (change, lookups) => {
    const fieldConfig = getHistoryFieldConfig(change.fieldName);
    const { label, type } = fieldConfig;
    const changeKind = getChangeKind(change.oldValue, change.newValue);
    const previousValue = formatHistoryValue(change.oldValue, type, lookups);
    const nextValue = formatHistoryValue(change.newValue, type, lookups);

    return {
        label,
        type,
        fieldName: change.fieldName,
        previousValue,
        nextValue,
        changeKind,
        isComment: type === 'comment',
        detail: buildDetailText({ label, previousValue, nextValue, changeKind }),
    };
};

const isCommentCreate = (event) =>
    event.entity === HISTORY_ENTITY.comment && event.type === HISTORY_EVENT_TYPE.create;

const isPtsHeaderEvent = (event) => event.entity === HISTORY_ENTITY.processedTimesheet;

const isSingleFieldUpdate = (event, details) =>
    event.type === HISTORY_EVENT_TYPE.update && details.length === 1;

const getCreateDeleteTitle = (event) => {
    if (event.entity === HISTORY_ENTITY.processedTimesheetEntry) {
        return event.type === HISTORY_EVENT_TYPE.delete ? 'Entry deleted' : 'New entry';
    }
    if (event.entity === HISTORY_ENTITY.processedMileageEntry) {
        return event.type === HISTORY_EVENT_TYPE.delete
            ? 'Mileage entry deleted'
            : 'New mileage entry';
    }
    if (event.entity === HISTORY_ENTITY.attachment) {
        return event.type === HISTORY_EVENT_TYPE.delete ? 'Attachment deleted' : 'Attachment added';
    }
    if (event.entity === HISTORY_ENTITY.comment) {
        return event.type === HISTORY_EVENT_TYPE.delete ? 'Comment deleted' : 'New Comment';
    }
    return event.type === HISTORY_EVENT_TYPE.delete ? 'Entry deleted' : 'Entry added';
};

const buildPtsSingleFieldHeading = (detail) => {
    if (STATUS_ONLY_FIELDS.has(detail.fieldName?.toLowerCase())) {
        return {
            headingOne: `${detail.label} changed from "${detail.previousValue}" to "${detail.nextValue}"`,
        };
    }
    if (detail.changeKind === CHANGE_KIND.added) {
        return { headingOne: `${detail.label} added: ${detail.nextValue}` };
    }
    if (detail.changeKind === CHANGE_KIND.removed) {
        return {
            headingOne: `${detail.label} removed: `,
            headingOneRemovedValue: detail.previousValue,
        };
    }
    return {
        headingOne: `${detail.label} updated: ${detail.previousValue} → ${detail.nextValue}`,
    };
};

const buildHeading = (event, details, lookups) => {
    const subjectDescription = buildSubjectDescription(event, lookups);

    if (isCommentCreate(event)) {
        return { headingOne: 'New Comment' };
    }

    if (event.type === HISTORY_EVENT_TYPE.delete || event.type === HISTORY_EVENT_TYPE.create) {
        return {
            headingOne: appendSubjectDescription(getCreateDeleteTitle(event), subjectDescription),
        };
    }

    if (isPtsHeaderEvent(event) && isSingleFieldUpdate(event, details)) {
        return buildPtsSingleFieldHeading(details[0]);
    }

    const fieldTitle =
        details.length === 1 ? `${details[0].label} updated` : `${details.length} fields updated`;

    return { headingOne: appendSubjectDescription(fieldTitle, subjectDescription) };
};

const getHeadingTwoPrefix = (event) => {
    if (isCommentCreate(event) || event.type === HISTORY_EVENT_TYPE.create) {
        return 'Created by';
    }
    if (event.type === HISTORY_EVENT_TYPE.delete) {
        return 'by';
    }
    return 'Modified by';
};

const buildHeadingTwo = (event) => {
    const formattedDate = event.modifiedAt
        ? formatDate(event.modifiedAt, { format: DATE_FORMATS.LT_DATE_AND_TIME })
        : BLANK_DISPLAY_VALUE;
    return `${getHeadingTwoPrefix(event)} ${getModifiedByDisplay(event.modifiedBy)} at ${
        formattedDate || BLANK_DISPLAY_VALUE
    }`;
};

const buildEventDetails = (event, lookups) => {
    if (isCommentCreate(event) && !isBlankValue(event.extra?.content)) {
        const commentValue = formatHistoryValue(event.extra.content, 'comment', lookups);
        return [
            {
                label: 'Comment',
                type: 'comment',
                fieldName: 'Comment',
                previousValue: BLANK_DISPLAY_VALUE,
                nextValue: commentValue,
                changeKind: CHANGE_KIND.added,
                isComment: true,
                detail: `Comment: ${commentValue}`,
            },
        ];
    }

    if (event.type !== HISTORY_EVENT_TYPE.update) {
        return [];
    }

    return event.changes.map((change) => mapChangeToDetail(change, lookups));
};

export const mapProcessedTimesheetHistory = (records = [], context = {}) => {
    if (!Array.isArray(records)) {
        return [];
    }

    const lookups = buildHistoryLookups(context);

    return records
        .map(normalizeHistoryEvent)
        .filter((event) => event.entity && event.type)
        .sort(
            (left, right) =>
                getModifiedAtTime(right.modifiedAt) - getModifiedAtTime(left.modifiedAt)
        )
        .flatMap((event) => {
            const details = buildEventDetails(event, lookups);
            if (event.type === HISTORY_EVENT_TYPE.update && details.length === 0) {
                return [];
            }

            const omitDetails =
                event.type === HISTORY_EVENT_TYPE.delete ||
                (event.type === HISTORY_EVENT_TYPE.create && !isCommentCreate(event)) ||
                (isPtsHeaderEvent(event) && isSingleFieldUpdate(event, details));
            const heading = buildHeading(event, details, lookups);

            return [
                {
                    headingOne: heading.headingOne,
                    headingOneRemovedValue: heading.headingOneRemovedValue,
                    headingTwo: buildHeadingTwo(event),
                    details: omitDetails ? [] : details,
                },
            ];
        });
};
